import { Link } from "react-router-dom";
import {
  MessageCircle,
  ArrowRight,
  Sparkles,
  Wheat,
  Flame,
  Droplet,
  Salad,
  Leaf,
  Cookie,
  Coffee,
  CheckCircle2,
  Users,
  Sun,
  Moon,
} from "lucide-react";
import Reveal from "../components/Reveal";
import { BRAND, waOrder } from "../lib/brand";

const THALI_IMG = "https://images.pexels.com/photos/9738980/pexels-photo-9738980.jpeg";
const SPICES_IMG = "https://images.pexels.com/photos/2632292/pexels-photo-2632292.jpeg";

const THALI = [
  { icon: Wheat, label: "4 Soft Rotis", note: "Fresh off the tawa — whole wheat, no maida." },
  { icon: Flame, label: "Seasonal Veg Sabzi", note: "Rotating daily. Always whole spices, light oil." },
  { icon: Droplet, label: "Dal or Curry", note: "Slow-simmered dal or a house curry." },
  { icon: Salad, label: "Steamed Rice", note: "Long-grain, cooked to fluffy perfection." },
  { icon: Leaf, label: "Fresh Salad", note: "Crisp veggies, lemon, a touch of chaat masala." },
  { icon: Cookie, label: "Fresh Dessert", note: "A small sweet ending — kheer, halwa or fruit." },
];

const CUSTOMIZATIONS = [
  "Extra rotis / extra rice",
  "Jain / no onion-garlic",
  "Low-oil / low-spice",
  "Raita instead of salad",
  "Add paneer / curd",
  "Double dessert for special days",
];

export default function MenuPage() {
  return (
    <div className="bg-[#faf8f5]">
      {/* Header */}
      <section className="relative pt-[120px] pb-16 md:pb-20 bg-[#1a2e5a] text-white overflow-hidden" data-testid="menu-header">
        <img
          src={SPICES_IMG}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-15"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a1530] via-[#1a2e5a]/85 to-[#1a2e5a]" />
        <div className="absolute inset-0 mandala-bg opacity-[0.05]" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <div className="text-[11px] tracking-[0.32em] uppercase text-[#c9a84c] font-bold mb-4">
              Our Menu
            </div>
          </Reveal>
          <Reveal delay={1}>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05]">
              Simple. Honest. <span className="italic text-[#c9a84c]">Home-made.</span>
            </h1>
          </Reveal>
          <Reveal delay={2}>
            <p className="font-body text-white/75 text-lg md:text-xl max-w-2xl mx-auto mt-6">
              The thali changes a little every day — because a good home
              kitchen never cooks the same thing twice in a row. Here's what
              you can expect.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Daily Thali Detail */}
      <section className="py-20 md:py-28" data-testid="menu-thali-detail">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5">
            <Reveal>
              <div className="relative">
                <div className="absolute -inset-3 bg-[#c9a84c]/20 rounded-[2rem] blur-xl" />
                <img
                  src={THALI_IMG}
                  alt="Home-cooked Indian thali"
                  className="relative rounded-[2rem] w-full h-[420px] md:h-[560px] object-cover border-2 border-[#c9a84c]/30 shadow-2xl"
                />
                <div className="absolute -top-5 -right-5 bg-[#c9a84c] text-[#1a2e5a] rounded-2xl px-5 py-3 shadow-xl">
                  <div className="text-[10px] tracking-[0.25em] uppercase font-black">Only</div>
                  <div className="font-display text-3xl font-bold leading-none">₹225</div>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="md:col-span-7">
            <Reveal>
              <div className="text-[11px] tracking-[0.32em] uppercase text-[#c9a84c] font-bold mb-3">
                The Daily Thali
              </div>
            </Reveal>
            <Reveal delay={1}>
              <h2 className="font-display text-4xl md:text-5xl font-bold text-[#1a2e5a] leading-tight mb-5">
                A complete meal. <span className="italic text-[#c9a84c]">Always ₹225.</span>
              </h2>
            </Reveal>
            <Reveal delay={2}>
              <p className="font-body text-slate-600 text-lg leading-relaxed mb-4">
                Each thali is plated fresh to order. No pre-packaged trays, no
                freezer bags. What you get at your door is what we'd serve at
                our own dinner table.
              </p>
            </Reveal>

            <Reveal delay={3}>
              <div className="flex flex-wrap gap-3 mb-8">
                <span className="inline-flex items-center gap-2 bg-[#1a2e5a] text-[#c9a84c] text-[11px] tracking-[0.2em] uppercase font-bold px-4 py-2 rounded-full">
                  <Sun size={12} /> Lunch
                </span>
                <span className="inline-flex items-center gap-2 bg-[#1a2e5a] text-[#c9a84c] text-[11px] tracking-[0.2em] uppercase font-bold px-4 py-2 rounded-full">
                  <Moon size={12} /> Dinner
                </span>
                <span className="inline-flex items-center gap-2 bg-white border border-[#c9a84c]/40 text-[#1a2e5a] text-[11px] tracking-[0.2em] uppercase font-bold px-4 py-2 rounded-full">
                  <Coffee size={12} /> Breakfast on Special Order
                </span>
              </div>
            </Reveal>

            <div className="space-y-3">
              {THALI.map((item, i) => {
                const Icon = item.icon;
                return (
                  <Reveal key={item.label} delay={((i % 6) + 1)}>
                    <div
                      className="card-lift flex items-start gap-4 bg-white rounded-2xl border border-[#c9a84c]/25 p-5"
                      data-testid={`menu-item-${i}`}
                    >
                      <div className="w-12 h-12 shrink-0 rounded-xl bg-[#1a2e5a] flex items-center justify-center">
                        <Icon size={20} className="text-[#c9a84c]" strokeWidth={1.8} />
                      </div>
                      <div>
                        <div className="font-display text-lg font-semibold text-[#1a2e5a]">
                          {item.label}
                        </div>
                        <div className="text-sm text-slate-600 font-body leading-relaxed">
                          {item.note}
                        </div>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Customisations */}
      <section className="py-16 md:py-24 bg-white border-y border-[#c9a84c]/20" data-testid="customisations-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div>
              <Reveal>
                <div className="text-[11px] tracking-[0.32em] uppercase text-[#c9a84c] font-bold mb-3">
                  Make It Yours
                </div>
              </Reveal>
              <Reveal delay={1}>
                <h2 className="font-display text-3xl md:text-4xl font-bold text-[#1a2e5a] leading-tight">
                  Customise your thali at a <span className="italic text-[#c9a84c]">nominal extra cost.</span>
                </h2>
              </Reveal>
            </div>
            <Reveal delay={2}>
              <span className="inline-flex items-center gap-2 bg-[#c9a84c] text-[#1a2e5a] text-[11px] font-black tracking-[0.2em] uppercase px-4 py-2 rounded-full w-fit">
                <Sparkles size={14} strokeWidth={2.5} />
                Nominal Extra Cost
              </span>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {CUSTOMIZATIONS.map((c, i) => (
              <Reveal key={c} delay={(i % 3) + 1}>
                <div className="flex items-center gap-3 bg-[#faf8f5] border border-[#c9a84c]/25 rounded-xl px-5 py-4 card-lift">
                  <CheckCircle2 size={18} className="text-[#c9a84c] shrink-0" />
                  <span className="font-body text-[#1a2e5a] text-sm">{c}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Monthly + Bulk summary */}
      <section className="py-20 md:py-28 bg-[#faf8f5]" data-testid="menu-packages">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-6 md:gap-8">
          <Reveal>
            <div className="card-lift rounded-3xl p-8 md:p-10 bg-[#1a2e5a] text-white h-full border border-[#c9a84c]/20">
              <div className="text-[11px] tracking-[0.32em] uppercase text-[#c9a84c] font-bold mb-3">
                Monthly Tiffin
              </div>
              <h3 className="font-display text-3xl md:text-4xl font-bold mb-4 leading-tight">
                Lower daily rate. <span className="italic text-[#c9a84c]">One less thing to think about.</span>
              </h3>
              <p className="font-body text-white/75 leading-relaxed mb-6">
                Subscribe monthly for lunch, dinner or both. Pause when you
                travel, resume when you're back. Get priority delivery slots
                and locked-in pricing.
              </p>
              <a
                href={waOrder("Hi! Please share the monthly tiffin subscription plans and pricing.")}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold"
                data-testid="menu-monthly-cta"
              >
                <MessageCircle size={16} strokeWidth={2.5} />
                Get Monthly Plan
              </a>
            </div>
          </Reveal>

          <Reveal delay={1}>
            <div className="card-lift rounded-3xl p-8 md:p-10 bg-white text-[#1a2e5a] h-full border border-[#c9a84c]/30">
              <div className="text-[11px] tracking-[0.32em] uppercase text-[#c9a84c] font-bold mb-3">
                Bulk & Events
              </div>
              <h3 className="font-display text-3xl md:text-4xl font-bold mb-4 leading-tight">
                Custom menus for <span className="italic text-[#c9a84c]">every occasion.</span>
              </h3>
              <p className="font-body text-slate-600 leading-relaxed mb-6">
                Kitty parties, birthdays, anniversaries, pujas, small family
                gatherings — we cook menus that feel personal. Share your
                guest count and dietary notes; we'll do the rest.
              </p>
              <a
                href={waOrder("Hi! I'd like a custom menu & quote for a bulk / event order.")}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-navy"
                data-testid="menu-bulk-cta"
              >
                <Users size={16} strokeWidth={2.5} />
                Enquire for Events
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA Strip */}
      <section className="py-16 md:py-20 bg-[#0a1530] text-white text-center" data-testid="menu-cta-strip">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal>
            <h3 className="font-display text-3xl md:text-4xl font-bold mb-5">
              Ready to eat <span className="italic text-[#c9a84c]">real food?</span>
            </h3>
          </Reveal>
          <Reveal delay={1}>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href={BRAND.whatsappChat}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold"
                data-testid="menu-whatsapp-cta"
              >
                <MessageCircle size={18} strokeWidth={2.5} />
                Order on WhatsApp
              </a>
              <Link to="/contact" className="btn-ghost-gold" data-testid="menu-to-contact">
                Talk to Us <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
